import { PageType } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { Headline } from "scmp-app/components/posties/content-homepage/headline";
import { WaveBottomBackground } from "scmp-app/components/posties/wave-background";
import { tracking } from "scmp-app/data";
import type { healthAndHappinessQuery$key } from "scmp-app/queries/__generated__/healthAndHappinessQuery.graphql";

import EmojiLeft from "./emoji-left.png";
import EmojiRight from "./emoji-right.png";
import { HealthAndHappinessItem } from "./health-and-happiness-item";
import PippaWithStars from "./pippa-with-stars.png";
import {
  Container,
  ContentContainer,
  EmojiBackgroundLeftContainer,
  EmojiBackgroundRightContainer,
  EmojiContainer,
  ItemsContainer,
  MainContainer,
  PippaWithStarsImage,
  PippaWithStarsImageContainer,
  StyledOvalUpperBackground,
  WaveBottomBackgroundContainer,
} from "./styles";

type Props = {
  reference: healthAndHappinessQuery$key;
};

export const HealthAndHappiness: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const data = useFragment(
    graphql`
      fragment healthAndHappinessQuery on Query {
        healthAndHappinessSection: section(filter: { entityId: "515935" }) {
          ...headlineSection
        }
        healthAndHappinessQueue: queue(filter: { name: "section_top_515935" }) {
          items(first: 3) {
            __typename
            edges {
              node {
                ... on Article {
                  ...healthAndHappinessItemQuery
                }
              }
            }
          }
        }
      }
    `,
    reference_,
  );

  const items = data?.healthAndHappinessQueue?.items?.edges;

  const customQueryParameters = {
    module: tracking.module.HealthHappiness,
    pgtype: tracking.pageType.Content,
  } as const;

  return (
    <Container>
      <MainContainer>
        <StyledOvalUpperBackground $ovalFillColor="#f8ffac" $rectFillColor="#0f75bf" />
        <ContentContainer>
          <Headline
            color="#0C635B"
            query={{
              pageType: PageType.PostiesSection,
              ...customQueryParameters,
            }}
            reference={data.healthAndHappinessSection}
          />
          <ItemsContainer>
            {items?.map((item, key) => (
              <HealthAndHappinessItem
                customQueryParameters={{
                  pageType: PageType.PostiesArticle,
                  ...customQueryParameters,
                }}
                index={key}
                key={key}
                reference={item?.node}
              />
            ))}
          </ItemsContainer>
          <EmojiContainer>
            <EmojiBackgroundLeftContainer>
              <img alt={EmojiLeft.alt} src={EmojiLeft.src} />
            </EmojiBackgroundLeftContainer>
            <EmojiBackgroundRightContainer>
              <img alt={EmojiRight.alt} src={EmojiRight.src} />
            </EmojiBackgroundRightContainer>
          </EmojiContainer>
        </ContentContainer>
      </MainContainer>
      <WaveBottomBackgroundContainer>
        <WaveBottomBackground $fillColor="#f8ffac" />
        <PippaWithStarsImageContainer>
          <PippaWithStarsImage alt={PippaWithStars.alt} src={PippaWithStars.src} />
        </PippaWithStarsImageContainer>
      </WaveBottomBackgroundContainer>
    </Container>
  );
};

HealthAndHappiness.displayName = "HealthAndHappiness";
